import React from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import OrderList from './components/OrderList';
import OrderDetailRoute from './components/orders/OrderDetailRoute';
import Settings from './components/Settings';
import StockTake from './components/StockTake';
import ReportProduction from './components/ReportProduction';
import ScanFlash from './components/common/ScanFlash';
import useAppUpdate from './hooks/useAppUpdate';
import SoundToggle from './components/common/SoundToggle';
import { getDomainConfig, BRAND_NAME } from './utils/domainConfig';
import { Package, ClipboardCheck, Factory, LogOut, Settings as SettingsIcon } from 'lucide-react';
import './App.css';

const domainConfig = getDomainConfig();

const MODULES = [
  { path: '/orders', label: 'Orders', Icon: Package },
  { path: '/stock-take', label: 'Stock Take', Icon: ClipboardCheck },
  { path: '/work-orders', label: 'Work Orders', Icon: Factory },
];

// Main app content (rendered inside auth context)
function AppContent() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { updateReady, reload } = useAppUpdate();

  const handleSignOut = async () => {
    await signOut();
  };

  const isFullMode = domainConfig.mode === 'full';
  // "/" redirects to Orders, so treat it as Orders for the active state
  // /orders/:orderId keeps the Orders tab active
  const isActive = (path) => location.pathname === path
    || location.pathname.startsWith(`${path}/`)
    || (path === '/orders' && location.pathname === '/');

  return (
    <div className={`app${isFullMode ? ' has-tab-bar' : ''}`}>
      <header className="app-header">
        <div className="app-header-content">
          <div className="app-header-text">
            <h1>
              <span className="app-title-full">{domainConfig.title}</span>
              <span className="app-title-short">{domainConfig.shortTitle}</span>
            </h1>
            <p>{domainConfig.description}</p>
          </div>
          <div className="header-actions">
            {isFullMode && (
              <nav className="header-nav" aria-label="Modules">
                {MODULES.map(({ path, label, Icon }) => (
                  <NavLink
                    key={path}
                    to={path}
                    className={`nav-link${isActive(path) ? ' active' : ''}`}
                    aria-current={isActive(path) ? 'page' : undefined}
                  >
                    <Icon size={16} aria-hidden="true" />
                    {label}
                  </NavLink>
                ))}
              </nav>
            )}
            <SoundToggle />
            {user && (
              <NavLink
                to="/settings"
                className={`header-icon-button${isActive('/settings') ? ' active' : ''}`}
                aria-label="Settings"
                title="Settings"
              >
                <SettingsIcon size={18} aria-hidden="true" />
              </NavLink>
            )}
            {user && (
              <button className="sign-out-button" onClick={handleSignOut} aria-label="Sign out">
                <LogOut size={16} aria-hidden="true" />
                <span className="sign-out-label">Sign Out</span>
              </button>
            )}
          </div>
        </div>
      </header>
      {updateReady && (
        <div className="update-banner" role="status">
          <span>A new version of the app is ready. It reloads by itself when the screen is left alone.</span>
          <button type="button" className="update-banner-button" onClick={reload}>
            <RefreshCw size={16} aria-hidden="true" /> Reload now
          </button>
        </div>
      )}
      <main className="app-main">
        {isFullMode ? (
          <Routes>
            <Route path="/orders" element={<OrderList />} />
            <Route path="/orders/:orderId" element={<OrderDetailRoute />} />
            <Route path="/stock-take" element={<StockTake />} />
            <Route path="/work-orders" element={<ReportProduction />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/" element={<Navigate to="/orders" replace />} />
            <Route path="*" element={<Navigate to="/orders" replace />} />
          </Routes>
        ) : (
          <Routes>
            {domainConfig.mode === 'orders' && <Route path="/orders" element={<OrderList />} />}
            {domainConfig.mode === 'orders' && <Route path="/orders/:orderId" element={<OrderDetailRoute />} />}
            {domainConfig.mode === 'stock-take' && <Route path="/stock-take" element={<StockTake />} />}
            {domainConfig.mode === 'work-orders' && <Route path="/work-orders" element={<ReportProduction />} />}
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to={domainConfig.defaultRoute} replace />} />
          </Routes>
        )}
      </main>
      {/* Phones and tablets: modules move from the header to a bottom tab bar */}
      {isFullMode && (
        <nav className="app-tab-bar" aria-label="Modules">
          {MODULES.map(({ path, label, Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={`app-tab${isActive(path) ? ' active' : ''}`}
              aria-current={isActive(path) ? 'page' : undefined}
            >
              <Icon size={24} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      )}
      <footer className="app-footer">
        <p>&copy; {new Date().getFullYear()} {BRAND_NAME} · {domainConfig.shortTitle}</p>
      </footer>
      <ScanFlash />
    </div>
  );
}

// Fallback shown when a render error is caught by the error boundary, so a single
// component crash shows a recovery screen instead of whitescreening the whole portal.
function ErrorFallback() {
  return (
    <div className="access-denied-page">
      <div className="access-denied-container" role="alert">
        <div className="access-denied-icon" aria-hidden="true">
          <AlertTriangle size={32} />
        </div>
        <h2>Something went wrong</h2>
        <p>An unexpected error occurred. Please reload the page to continue.</p>
        <button
          className="access-denied-button"
          onClick={() => window.location.reload()}
        >
          <RefreshCw size={16} aria-hidden="true" />
          Reload
        </button>
      </div>
    </div>
  );
}

// Root app with auth providers and router
function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <AuthProvider>
          <ProtectedRoute requiredApp="dispatch">
            <AppContent />
          </ProtectedRoute>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
